import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  Button,
  Label,
  FormGroup
} from "reactstrap";

import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import Alerts from "../../components/Alerts/alerts";

import ComplaintsService from "./complaints.service";
import CustomerService from "../Edsha/Customers/Customer.service";
import AuthService from "../Authentication/auth.service";

const AddComplaint = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);

  const [formData, setFormData] = useState({
    external_customer: false,
    customer_id: '',
    customer_name: '',
    customer_email: '',
    recipient_customer_emails: [],
    category: '',
    priority: '',
    subject: '',
    description: '',
    order_id: '',
    attachments: []
  });

  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [recipientEmailInput, setRecipientEmailInput] = useState("");
  const [recipientInputFocused, setRecipientInputFocused] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchPriorities();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!formData.external_customer && formData.customer_id) {
      fetchCustomerOrders(formData.customer_id);
    } else {
      setOrders([]);
    }
  }, [formData.customer_id, formData.external_customer]);

  const fetchCategories = () => {
    ComplaintsService.getCategories().then((response) => {
      if (response.data.status) {
        setCategories(response.data.data);
      }
    });
  };

  const fetchPriorities = () => {
    ComplaintsService.getPriorities().then((response) => {
      if (response.data.status) {
        setPriorities(response.data.data);
      }
    });
  };

  const fetchCustomers = () => {
    var payload = {
      functionName: "GetCustomers",
      postData: {
        sort: { _id: -1 },
        offset: 0,
        limit: 100000,
        filter: {}
      }
    };
    CustomerService.getAllCustomers(payload).then((response) => {
      if (response.data.status) {
        setCustomers(response.data.data);
      }
    });
  };

  const fetchCustomerOrders = (customerId) => {
    var payload = {
      functionName: "GetCustomerOrders",
      postData: {
        limit: 100000,
        offset: 0,
        filter: {
          customer_id: customerId
        }
      }
    };
    setCustomerLoading(true);
    CustomerService.getCustomerOrders(payload).then((response) => {
      setCustomerLoading(false);
      if (response.data.status) {
        setOrders(response.data.data || []);
      }
    }, (error) => {
      setCustomerLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      Alerts.swalErrorAlert(errContent || "Failed to fetch customer orders");
    }).finally(() => {
      setCustomerLoading(false);
    });
  };

  const normalizeAndUniqueEmails = (emails = []) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const seen = new Set();
    const unique = [];
    (emails || []).forEach((email) => {
      const normalized = (email || "").toString().trim().toLowerCase();
      if (!normalized || !emailRegex.test(normalized) || seen.has(normalized)) return;
      seen.add(normalized);
      unique.push(normalized);
    });
    return unique;
  };

  const autoFillRecipientEmailsForCustomer = (customerId, customerEmail) => {
    if (!customerId) {
      setFormData((prev) => ({ ...prev, recipient_customer_emails: [] }));
      return;
    }
    const getContactsPayload = {
      functionName: "GetUserContacts",
      postData: {
        user_id: customerId
      }
    };
    CustomerService.GetUserContacts(getContactsPayload).then((response) => {
      const contacts = (response.data && response.data.data) || [];
      const contactEmails = contacts.map((contact) => contact.email).filter(Boolean);
      const mergedEmails = normalizeAndUniqueEmails([customerEmail, ...contactEmails]);
      setFormData((prev) => ({
        ...prev,
        recipient_customer_emails: mergedEmails
      }));
    }).catch(() => {
      const mergedEmails = normalizeAndUniqueEmails([customerEmail]);
      setFormData((prev) => ({
        ...prev,
        recipient_customer_emails: mergedEmails
      }));
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));

    if (name === "external_customer") {
      if (checked) {
        setFormData(prev => ({
          ...prev,
          external_customer: true,
          customer_id: "",
          order_id: "",
          recipient_customer_emails: []
        }));
        setOrders([]);
      } else {
        setFormData(prev => ({
          ...prev,
          external_customer: false,
          customer_name: "",
          customer_email: "",
          recipient_customer_emails: []
        }));
      }
    }

    if (name === "customer_id" && !formData.external_customer) {
      const selectedCustomer = customers.find((customer) => customer._id === value);
      autoFillRecipientEmailsForCustomer(value, selectedCustomer?.email || "");
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: files
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const addRecipientEmail = (rawEmail) => {
    const email = (rawEmail || "").trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      Alerts.swalErrorAlert("Please enter a valid recipient email");
      return;
    }
    if (formData.recipient_customer_emails.includes(email)) {
      setRecipientEmailInput("");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      recipient_customer_emails: [...prev.recipient_customer_emails, email]
    }));
    setRecipientEmailInput("");
  };

  const removeRecipientEmail = (emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      recipient_customer_emails: prev.recipient_customer_emails.filter((email) => email !== emailToRemove)
    }));
  };

  const handleRecipientEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addRecipientEmail(recipientEmailInput);
    }
    if (e.key === "Backspace" && !recipientEmailInput && formData.recipient_customer_emails.length > 0) {
      const emails = [...formData.recipient_customer_emails];
      const lastEmail = emails.pop();
      setFormData((prev) => ({
        ...prev,
        recipient_customer_emails: emails
      }));
      setRecipientEmailInput(lastEmail || "");
    }
  };

  // Convert files to Base64 (similar to profile_image pattern)
  const convertFilesToBase64 = (files) => {
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            resolve({
              filename: file.name,
              data: reader.result // Base64 data URL: "data:mime/type;base64,..."
            });
          };
          reader.onerror = (error) => reject(error);
        });
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missingCustomerInput = formData.external_customer
      ? (!formData.customer_name || !formData.customer_email)
      : !formData.customer_id;

    if (missingCustomerInput || !formData.category || !formData.priority ||
      !formData.subject || !formData.description) {
      Alerts.swalErrorAlert("Please fill in all required fields");
      return;
    }

    if (!formData.recipient_customer_emails || formData.recipient_customer_emails.length === 0) {
      Alerts.swalErrorAlert("Please add at least one recipient customer email");
      return;
    }

    if (formData.external_customer) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customer_email)) {
        Alerts.swalErrorAlert("Please enter a valid customer email");
        return;
      }
    }

    setLoading(true);

    // Convert files to Base64 before sending
    let base64Attachments = [];
    if (formData.attachments && formData.attachments.length > 0) {
      try {
        base64Attachments = await convertFilesToBase64(formData.attachments);
      } catch (error) {
        setLoading(false);
        Alerts.swalErrorAlert("Failed to convert files to Base64");
        return;
      }
    }

    const submitData = {
      ...formData,
      attachments: base64Attachments
    };

    ComplaintsService.createComplaint(submitData).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Complaint created successfully");
        navigate('/complaints-list');
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to create complaint");
      }
    }, (error) => {
      setLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      Alerts.swalErrorAlert(errContent || "Server Error, Please try again later.");
    });
  };

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="app-page-title">
            <div className="page-title-wrapper justify-content-left">
              <div className="page-title-heading fw-bold">
                <div className="page-title-icon">
                  <i className="pe-7s-plus icon-gradient bg-happy-green" />
                </div>
                <div>
                  {t('Create Complaint')}
                  <div className="page-title-subheading">
                    {t('Fill the form below to create a new complaint.')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center">
            <div className="col-md-12">
              <Card className="main-card mb-3">
                <CardBody>
                  <LoadingOverlay tag="div" active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}>
                    <form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <Label className="mb-0">
                                {formData.external_customer ? 'Customer Name *' : 'Customer *'}
                              </Label>
                              <Label check className="mb-0" style={{ fontWeight: 400 }}>
                                <Input
                                  type="checkbox"
                                  name="external_customer"
                                  checked={!!formData.external_customer}
                                  onChange={handleInputChange}
                                />
                                {' '}External Customer
                              </Label>
                            </div>

                            {!formData.external_customer ? (
                              <Input
                                type="select"
                                name="customer_id"
                                value={formData.customer_id}
                                onChange={handleInputChange}
                                required
                                searchable={true}
                              >
                                <option value="">Select Customer</option>
                                {customers.map((customer, index) => (
                                  <option key={index} value={customer._id}>
                                    {customer.company_name || customer.name} - {customer.email}
                                  </option>
                                ))}
                              </Input>
                            ) : (
                              <Input
                                type="text"
                                name="customer_name"
                                value={formData.customer_name}
                                onChange={handleInputChange}
                                required
                              />
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup style={{ position: 'relative' }}>
                            <Label>{formData.external_customer ? 'Customer Email *' : 'Related Order (Optional)'}</Label>
                            {!formData.external_customer && customerLoading && (
                              <div style={{ position: 'absolute', top: 32, right: 40, zIndex: 1000, width: '30px', height: '30px' }}>
                                <DNALoader height={'30px'} width={'30px'} />
                              </div>
                            )}
                            {!formData.external_customer ? (
                              <Input
                                type="select"
                                name="order_id"
                                value={formData.order_id}
                                onChange={handleInputChange}
                                disabled={customerLoading}
                              >
                                <option value="">None</option>
                                {orders.map((order, index) => (
                                  <option key={index} value={order._id}>
                                    {order.order_no || order._id}
                                  </option>
                                ))}
                              </Input>
                            ) : (
                              <Input
                                type="email"
                                name="customer_email"
                                value={formData.customer_email}
                                onChange={handleInputChange}
                                required
                              />
                            )}
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label>Recipient Customer Emails *</Label>
                            <div
                              style={{
                                border: recipientInputFocused ? "1px solid #80bdff" : "1px solid #ced4da",
                                borderRadius: "4px",
                                minHeight: "38px",
                                padding: "4px 8px",
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: "6px",
                                backgroundColor: "#fff",
                                boxShadow: recipientInputFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none"
                              }}
                              onClick={() => document.getElementById("recipient-customer-emails-input")?.focus()}
                            >
                              {(formData.recipient_customer_emails || []).map((email) => (
                                <div
                                  key={email}
                                  style={{
                                    border: "1px solid #d6d8db",
                                    borderRadius: "14px",
                                    padding: "2px 8px",
                                    background: "#f1f3f5",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    maxWidth: "100%"
                                  }}
                                >
                                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeRecipientEmail(email)}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      color: "#dc3545",
                                      cursor: "pointer",
                                      fontSize: "14px",
                                      lineHeight: 1,
                                      padding: 0
                                    }}
                                    aria-label={`Remove ${email}`}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <input
                                id="recipient-customer-emails-input"
                                type="text"
                                value={recipientEmailInput}
                                onChange={(e) => setRecipientEmailInput(e.target.value)}
                                onKeyDown={handleRecipientEmailKeyDown}
                                onBlur={() => {
                                  setRecipientInputFocused(false);
                                  addRecipientEmail(recipientEmailInput);
                                }}
                                onFocus={() => setRecipientInputFocused(true)}
                                placeholder={formData.recipient_customer_emails.length ? "" : "Type email and press Enter"}
                                style={{
                                  border: "none",
                                  outline: "none",
                                  flex: 1,
                                  minWidth: "220px",
                                  padding: "4px 2px",
                                  background: "transparent"
                                }}
                              />
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Category *</Label>
                            <Input
                              type="select"
                              name="category"
                              value={formData.category}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Category</option>
                              {categories.map((category, index) => (
                                <option key={index} value={category.value}>
                                  {category.name}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Priority *</Label>
                            <Input
                              type="select"
                              name="priority"
                              value={formData.priority}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Priority</option>
                              {priorities.map((priority, index) => (
                                <option key={index} value={priority.value}>
                                  {priority.name}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label>Subject *</Label>
                            <Input
                              type="text"
                              name="subject"
                              value={formData.subject}
                              onChange={handleInputChange}
                              required
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label>Description *</Label>
                            <Input
                              type="textarea"
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              rows="5"
                              required
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label>Attachments (Optional)</Label>
                            <Input
                              type="file"
                              multiple
                              onChange={handleFileChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12} className="text-end">
                          <Button color="secondary" className="me-2" onClick={() => navigate('/complaints-list')}>
                            Cancel
                          </Button>
                          <Button color="primary" type="submit">
                            Create Complaint
                          </Button>
                        </Col>
                      </Row>
                    </form>
                  </LoadingOverlay>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddComplaint;

